import React, { useMemo } from 'react';

interface CarRangeSliderProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  valueMin: number;
  valueMax: number;
  formatValue: (value: number) => string;
  formatRange?: (minValue: number, maxValue: number) => string;
  minAriaLabel: string;
  maxAriaLabel: string;
  onChange: (nextMin: number, nextMax: number) => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export const CarRangeSlider: React.FC<CarRangeSliderProps> = ({
  label,
  min,
  max,
  step = 1,
  valueMin,
  valueMax,
  formatValue,
  formatRange,
  minAriaLabel,
  maxAriaLabel,
  onChange,
}) => {
  const lo = clamp(Math.min(valueMin, valueMax), min, max);
  const hi = clamp(Math.max(valueMin, valueMax), min, max);
  const span = max - min;
  const fill = useMemo(() => {
    if (span <= 0) return { left: '0%', width: '100%' };
    const left = ((lo - min) / span) * 100;
    const right = ((hi - min) / span) * 100;
    return { left: `${left}%`, width: `${Math.max(right - left, 0)}%` };
  }, [hi, lo, min, span]);

  if (span <= 0) return null;

  return (
    <div className="car-filter-field">
      <span className="car-filter-label">{label}</span>
      <p className="car-range-values">
        {formatRange ? formatRange(lo, hi) : `${formatValue(lo)} to ${formatValue(hi)}`}
      </p>
      <div className="car-range-track">
        <div className="car-range-fill" style={fill} />
        <input
          type="range"
          className="car-range-input car-range-input--min"
          min={min}
          max={max}
          step={step}
          value={lo}
          aria-label={minAriaLabel}
          onChange={(event) => {
            const next = Number(event.target.value);
            onChange(Math.min(next, hi), hi);
          }}
        />
        <input
          type="range"
          className="car-range-input car-range-input--max"
          min={min}
          max={max}
          step={step}
          value={hi}
          aria-label={maxAriaLabel}
          onChange={(event) => {
            const next = Number(event.target.value);
            onChange(lo, Math.max(next, lo));
          }}
        />
      </div>
    </div>
  );
};
